import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEqualWith } from "lodash";
import ReactDOM from "react-dom";
import { useHistory, useParams } from "react-router-dom";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrashCan, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ILocation, IModule } from "redux/reducers/models/reservations";

import { useAppDispatch, useAppSelector } from "hooks/redux";

import { ButtonNew as Button } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import ScrollTabsLayout, { TSection } from "components/scrollTabsLayout/ScrollTabsLayout";
import Toggle from "components/form/toggle/Toggle";
import TimePick from "components/timePick/TimePick";
import TextEditor from "components/textEditor/textEditor";

import "./activitySettings.scss";
import {
  DeleteReservationLocation,
  DeleteReservationModule,
  GetReservationLocation,
  GetReservationModule,
  PostReservationLocation,
  PutReservationLocation,
  PutReservationModule,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import {
  GetFacilityTeeSheetSettings,
  IPutTeeSheetSettings,
  PutFacilityTeeSheetSettings,
} from "api/rpc/2024-04/facilityAdmin/facility/settings";
import { loadFacilityTeeSheetSettings } from "redux/actions/facility";
import TagInput from "components/tagInput/TagInput";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import { unitLabel } from "./unitLabel";
import useDebounce from "hooks/events/useDebounce";

interface IUnitDraft {
  key: string;
  id: number | null;
  title: string;
  savedTitle: string;
}

interface IState {
  module: IModule;
}

interface IModuleState {
  moduleTitle: string;
  webBookingEnabled: boolean;
  display_online_pricing?: boolean;
  editWindow: string;
  cancellationWindow: string;
  daysInAdvanceStart: number;
  daysInAdvanceEnd: number;
  openTime: string;
  payment_terms: string;
  booking_terms: string;
  bufferWindow: string;
  maxBookingSize: number | string;
  requireBookingApproval: boolean;
  reservationConfirmations: boolean;
  reservationConfirmationEmail: string;
}


function parseEmails(value?: string): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(/[,;\n]+/)
    .map(email => email.trim())
    .filter(Boolean);
}

function sectionMatchesSearch(section: TSection, query: string) {
  if (!query) {
    return true;
  }

  const searchableText = [section.label, ...(section.keywords ?? [])].join(" ").toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .every(word => searchableText.includes(word));
}

function unitDraftsFromLocations(locations: Array<ILocation>): IUnitDraft[] {
  return (locations ?? []).map(location => ({
    key: String(location.id),
    id: location.id,
    title: location.title ?? "",
    savedTitle: location.title ?? "",
  }));
}

export default function ConfigSegment() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const { authStore, facilityStore } = useAppSelector(store => store);
  const permissions = authStore?.user?.permissions;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [link, setLink] = useState<{ clientShortName: string; facilityShortName: string }>({
    clientShortName: "",
    facilityShortName: "",
  });

  const dispatch = useAppDispatch();

  const {
    state: deleteModuleState,
    updateModal: updateDeleteModuleState,
    closeModal: closeDeleteModuleState,
  } = useModal();
  const {
    state: deleteLocationState,
    updateModal: updateDeleteLocationState,
    closeModal: closeDeleteLocationState,
  } = useModal<{ id: number | null; title: string }>({ id: null, title: "" });

  const [state, setState] = useState<IState>({
    module: null,
  });
  const [units, setUnits] = useState<IUnitDraft[]>([]);
  const [unitsBeforeChanges, setUnitsBeforeChanges] = useState<IUnitDraft[]>([]);
  const [newLocationName, setNewLocationName] = useState("");
  const [editingLocationKey, setEditingLocationKey] = useState<string | null>(null);
  const [editingLocationSnapshot, setEditingLocationSnapshot] = useState("");
  const [addingLocation, setAddingLocation] = useState(false);
  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const skipLocationBlur = useRef(false);

  const [moduleState, setModuleState] = useState<IModuleState>({
    moduleTitle: "",
    webBookingEnabled: false,
    display_online_pricing: false,
    editWindow: "",
    cancellationWindow: "",
    daysInAdvanceStart: null,
    daysInAdvanceEnd: null,
    openTime: "0:00",
    booking_terms: "",
    payment_terms: "",
    bufferWindow: null,
    maxBookingSize: "",
    requireBookingApproval: false,
    reservationConfirmations: false,
    reservationConfirmationEmail: "",
  });

  const [moduleStateBeforeChanges, setModuleStateBeforeChanges] = useState<IModuleState>(undefined);
  const [moduleLoaded, setModuleLoaded] = useState<boolean>(false);
  const [reservationEmailDraft, setReservationEmailDraft] = useState("");

  useEffect(() => {
    void getModule();
  }, []);

  useEffect(() => {
    if (facilityStore?.facility) {
      setLink(prevState => ({
        ...prevState,
        clientShortName: facilityStore?.facility?.client?.short_name,
        facilityShortName: facilityStore?.facility?.short_name,
      }));
    }
  }, [facilityStore?.facility]);

  async function getModule() {
    dispatch(enqueue());
    const moduleRes = await GetReservationModule({ id: Number(id) }, false);

    if (moduleRes?.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      dispatch(showError(t("Error getting activity types")));
      dispatch(dequeue());
      return;
    }

    const locationRes = await GetReservationLocation({ reservation_module_id: Number(id) }, false);
    if (locationRes.status !== StatusCode.OK) {
      history.push("/admin/settings/reservations/modules");
      dispatch(showError(t("Error getting units")));
      dispatch(dequeue());
      return;
    }
    const settingsRes = await GetFacilityTeeSheetSettings(undefined, false);
    dispatch(dequeue());

    const locations: ILocation[] = Array.isArray(locationRes.data) ? (locationRes.data as ILocation[]) : [];
    const nextUnits = unitDraftsFromLocations(locations);
    const confirmationSettings =
      settingsRes.status === StatusCode.OK
        ? (settingsRes.data as IPutTeeSheetSettings)
        : null;
    const nextModuleState: IModuleState = {
      moduleTitle: moduleRes?.data?.title,
      webBookingEnabled: moduleRes?.data?.enable_web_booking,
      cancellationWindow: moduleRes?.data?.web_cancellation_hours,
      editWindow: moduleRes?.data?.web_edit_hours,
      daysInAdvanceStart: moduleRes?.data?.days_in_advance_start,
      daysInAdvanceEnd: moduleRes?.data?.days_in_advance_end,
      openTime: moduleRes?.data?.open_time,
      booking_terms: moduleRes?.data?.booking_terms,
      payment_terms: moduleRes?.data?.payment_terms,
      bufferWindow: moduleRes?.data?.buffer_window_hours,
      display_online_pricing:
        moduleRes?.data?.display_online_pricing === null ? false : moduleRes?.data?.display_online_pricing,
      maxBookingSize: moduleRes?.data?.max_booking_size ?? 10,
      requireBookingApproval: !!moduleRes?.data?.require_booking_approval,
      reservationConfirmations: !!confirmationSettings?.enable_internal_reservation_confirmations,
      reservationConfirmationEmail:
        confirmationSettings?.internal_reservation_confirmation_email == null
          ? ""
          : String(confirmationSettings.internal_reservation_confirmation_email),
    };

    ReactDOM.unstable_batchedUpdates(() => {
      setState({ module: moduleRes.data });
      setUnits(nextUnits);
      setUnitsBeforeChanges(nextUnits.map(unit => ({ ...unit })));
      setModuleState(nextModuleState);
      setModuleStateBeforeChanges(nextModuleState);
      setReservationEmailDraft("");
      setModuleLoaded(true);
    });
  }

  function handleModuleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setModuleState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setModuleState(prevState => ({ ...prevState, [id]: checked }));
  }

  function addReservationEmail(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter" && event.key !== ",") {
      return;
    }
    event.preventDefault();
    const existing = parseEmails(moduleState.reservationConfirmationEmail);
    const next = reservationEmailDraft.trim().replace(/,$/, "");
    if (!next || existing.includes(next)) {
      setReservationEmailDraft("");
      return;
    }
    setModuleState(prevState => ({
      ...prevState,
      reservationConfirmationEmail: [...existing, next].join(", "),
    }));
    setReservationEmailDraft("");
  }

  function removeReservationEmail(index: number) {
    const existing = parseEmails(moduleState.reservationConfirmationEmail);
    setModuleState(prevState => ({
      ...prevState,
      reservationConfirmationEmail: existing.filter((_, emailIndex) => emailIndex !== index).join(", "),
    }));
  }

  function unitsChanged() {
    return units.some(unit => {
      if (!unit.id) {
        return unit.title.trim().length > 0;
      }
      const before = unitsBeforeChanges.find(item => item.id === unit.id);
      return !before || before.title !== unit.title;
    });
  }

  function unsavedChangesExist() {
    if (moduleStateBeforeChanges === undefined) {
      if (moduleLoaded) {
        setModuleStateBeforeChanges(moduleState);
      }
      return false;
    }

    return !isEqualWith(moduleState, moduleStateBeforeChanges) || unitsChanged();
  }

  async function saveUnits() {
    const name = unitLabel(moduleState.moduleTitle, t("Location"));
    if (units.some(unit => unit.id && !unit.title.trim())) {
      dispatch(showError(t("Enter a name")));
      return false;
    }

    for (const unit of units) {
      const title = unit.title.trim();
      if (!unit.id) {
        if (!title) {
          continue;
        }
        const created = await PostReservationLocation(
          { title, reservation_module_id: Number(id) },
          false,
        );
        if (created?.status !== StatusCode.OK) {
          dispatch(showError(t("Error saving {{name}}", { name })));
          return false;
        }
        continue;
      }

      if (title === unit.savedTitle) {
        continue;
      }
      const updated = await PutReservationLocation(
        { id: unit.id, title, reservation_module_id: Number(id) },
        false,
      );
      if (updated?.status !== StatusCode.OK) {
        dispatch(showError(t("Error saving {{name}}", { name })));
        return false;
      }
    }

    return true;
  }

  function addLocation() {
    const title = newLocationName.trim();
    if (!title || !permissions?.reservations_location_new) {
      return;
    }
    setUnits(prev => [...prev, { key: `new-${Date.now()}`, id: null, title, savedTitle: "" }]);
    setNewLocationName("");
  }

  function removeNewLocation(key: string) {
    setUnits(prev => prev.filter(unit => unit.key !== key || unit.id !== null));
    if (editingLocationKey === key) {
      setEditingLocationKey(null);
    }
  }

  function canEditLocation(unit: IUnitDraft) {
    return unit.id ? !!permissions?.reservations_location_edit : !!permissions?.reservations_location_new;
  }

  function startEditingLocation(unit: IUnitDraft) {
    if (!canEditLocation(unit)) {
      return;
    }
    skipLocationBlur.current = false;
    setAddingLocation(false);
    setNewLocationName("");
    setEditingLocationKey(unit.key);
    setEditingLocationSnapshot(unit.title);
  }

  function finishEditingLocation(title: string) {
    if (skipLocationBlur.current) {
      skipLocationBlur.current = false;
      return;
    }
    const key = editingLocationKey;
    if (!key) {
      return;
    }
    const nextTitle = title.trim() || editingLocationSnapshot;
    setUnits(prev => prev.map(item => (item.key === key ? { ...item, title: nextTitle } : item)));
    setEditingLocationKey(null);
  }

  function cancelEditingLocation() {
    skipLocationBlur.current = true;
    const key = editingLocationKey;
    const snapshot = editingLocationSnapshot;
    if (key) {
      setUnits(prev => prev.map(item => (item.key === key ? { ...item, title: snapshot } : item)));
    }
    setEditingLocationKey(null);
  }

  function finishAddingLocation() {
    if (skipLocationBlur.current) {
      skipLocationBlur.current = false;
      return;
    }
    skipLocationBlur.current = true;
    if (newLocationName.trim()) {
      addLocation();
    }
    setAddingLocation(false);
    setNewLocationName("");
  }

  function cancelAddingLocation() {
    skipLocationBlur.current = true;
    setAddingLocation(false);
    setNewLocationName("");
  }

  useEffect(() => {
    if (editingLocationKey || addingLocation) {
      locationInputRef.current?.focus();
      locationInputRef.current?.select();
    }
  }, [editingLocationKey, addingLocation]);

  async function saveModule() {
    if (!moduleState?.moduleTitle) {
      dispatch(showError(t("Enter an activity type title")));
      return;
    }

    const moduleRes = await PutReservationModule(
      {
        id: Number(id),
        enable_web_booking: moduleState?.webBookingEnabled,
        web_edit_hours: Number(moduleState?.editWindow),
        web_cancellation_hours: Number(moduleState?.cancellationWindow),
        days_in_advance_start: Number(moduleState?.daysInAdvanceStart),
        days_in_advance_end: Number(moduleState?.daysInAdvanceEnd),
        open_time: moduleState?.openTime,
        title: moduleState?.moduleTitle,
        booking_terms: moduleState?.booking_terms,
        payment_terms: moduleState?.payment_terms,
        display_online_pricing: moduleState?.display_online_pricing,
        buffer_window_hours: moduleState?.bufferWindow,
        max_booking_size: Number(moduleState?.maxBookingSize) || 10,
        require_booking_approval: !!moduleState?.requireBookingApproval,
      },
      true,
    );
    if (moduleRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating activity type")));
      return;
    }

    const confirmationsChanged =
      moduleState.reservationConfirmations !== moduleStateBeforeChanges?.reservationConfirmations ||
      moduleState.reservationConfirmationEmail !== moduleStateBeforeChanges?.reservationConfirmationEmail;
    if (confirmationsChanged) {
      const settingsRes = await PutFacilityTeeSheetSettings(
        {
          enable_internal_reservation_confirmations: moduleState.reservationConfirmations,
          internal_reservation_confirmation_email: moduleState.reservationConfirmationEmail,
        } as IPutTeeSheetSettings,
        false,
      );
      if (settingsRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating reservation confirmations")));
        void getModule();
        return;
      }
      void dispatch(loadFacilityTeeSheetSettings());
    }

    const unitsSaved = await saveUnits();
    if (!unitsSaved) {
      void getModule();
      return;
    }
    dispatch(showSuccess(t("Activity type updated successfully")));
    void getModule();
  }

  function handleBookingTermsChange(terms: string) {
    setModuleState(prevState => ({ ...prevState, booking_terms: terms }));
  }

  function handlePaymentTermsChange(terms: string) {
    setModuleState(prevState => ({ ...prevState, payment_terms: terms }));
  }

  async function writeTextToClipBoard(link: string) {
    await navigator.clipboard.writeText(link);
    dispatch(showSuccess(t("Copied link to clipboard")));
  }

  async function deleteLocation() {
    const locationId = deleteLocationState?.id;
    if (!locationId) {
      return;
    }

    const res = await DeleteReservationLocation({ id: locationId }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res.data : t("Error deleting location")));
      return;
    }

    dispatch(showSuccess(t("Location deleted successfully")));
    void closeDeleteLocationState();
    setUnits(prev => prev.filter(unit => unit.id !== locationId));
    setUnitsBeforeChanges(prev => prev.filter(unit => unit.id !== locationId));
    if (editingLocationKey && units.some(unit => unit.id === locationId && unit.key === editingLocationKey)) {
      setEditingLocationKey(null);
    }
  }

  async function deleteModule() {
    const res = await DeleteReservationModule({ id: Number(id) }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting activity type")));
      return;
    }
    dispatch(showSuccess(t("Activity type deleted successfully")));
    void closeDeleteModuleState();
    history.push("/admin/settings/reservations/modules");
  }

  const bookingLink = window.origin + `/portal/${link?.clientShortName}/reservations/${link.facilityShortName}`;
  const moduleLocked = !permissions?.reservations_module_edit;
  const settingsSections = (
    [
      {
        id: "general-settings",
        label: t("General Settings"),
        keywords: ["title", "name", "activity type", "web booking", "online pricing", "display"],
        content: (
          <div className="activity-settings-general">
            <div className="activity-settings-title">
              <Input
                value={moduleState.moduleTitle ?? ""}
                label={t("Activity Type")}
                id="moduleTitle"
                onChange={handleModuleInputChange}
                placeholder={t("Activity Type")}
                disabled={moduleLocked}
              />
            </div>
            <div className="activity-settings-toggles">
              <Toggle
                labelRight={t("Enable Web Booking")}
                id="webBookingEnabled"
                checked={!!moduleState.webBookingEnabled}
                onChange={handleToggleChange}
                disabled={moduleLocked}
              />
              <Toggle
                labelRight={t("Display Online Pricing")}
                id="display_online_pricing"
                checked={!!moduleState.display_online_pricing}
                onChange={handleToggleChange}
                disabled={moduleLocked}
              />
            </div>
          </div>
        ),
      },
      {
        id: "locations",
        label: t("Locations"),
        keywords: ["location", "locations", "unit", "bay", "name"],
        content: (
          <div className="activity-locations">
            {units.map(unit => {
              const editing = editingLocationKey === unit.key;
              const editable = canEditLocation(unit);
              return (
                <div
                  key={unit.key}
                  className={`activity-locations-row${editing ? " activity-locations-row--editing" : ""}`}
                >
                  {editing ? (
                    <div className="activity-locations-field">
                      <Input
                        id={`location-${unit.key}`}
                        label={t("Name")}
                        labelHidden
                        value={unit.title}
                        inputRef={locationInputRef}
                        onChange={event =>
                          setUnits(prev =>
                            prev.map(item => (item.key === unit.key ? { ...item, title: event.target.value } : item)),
                          )
                        }
                        onBlur={event => finishEditingLocation(event.target.value == null ? "" : String(event.target.value))}
                        onKeyDown={event => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                          if (event.key === "Escape") {
                            event.preventDefault();
                            cancelEditingLocation();
                          }
                        }}
                      />
                    </div>
                  ) : editable ? (
                    <button type="button" className="activity-locations-name" onClick={() => startEditingLocation(unit)}>
                      {unit.title}
                    </button>
                  ) : (
                    <span className="activity-locations-name">{unit.title}</span>
                  )}
                  {!unit.id ? (
                    <button
                      type="button"
                      className="activity-locations-icon activity-locations-icon--visible"
                      aria-label={t("Remove")}
                      onClick={() => removeNewLocation(unit.key)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  ) : editable ? (
                    <div className="activity-locations-actions">
                      {!editing ? (
                        <button
                          type="button"
                          className="activity-locations-icon"
                          aria-label={t("Edit")}
                          onClick={() => startEditingLocation(unit)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="activity-locations-icon"
                        aria-label={t("Delete")}
                        onClick={() => updateDeleteLocationState({ isOpen: true, id: unit.id, title: unit.title })}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                  ) : (
                    <span className="activity-locations-spacer" />
                  )}
                </div>
              );
            })}
            {permissions?.reservations_location_new ? (
              addingLocation ? (
                <div className="activity-locations-row activity-locations-row--editing">
                  <div className="activity-locations-field">
                    <Input
                      id="new-location"
                      label={t("Location name")}
                      labelHidden
                      value={newLocationName}
                      placeholder={t("Location name")}
                      inputRef={locationInputRef}
                      onChange={event => setNewLocationName(event.target.value)}
                      onBlur={finishAddingLocation}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addLocation();
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          cancelAddingLocation();
                        }
                      }}
                    />
                  </div>
                  <span className="activity-locations-spacer" />
                </div>
              ) : (
                <button
                  type="button"
                  className="activity-locations-add"
                  onClick={() => {
                    skipLocationBlur.current = false;
                    setEditingLocationKey(null);
                    setAddingLocation(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t("Add location")}
                </button>
              )
            ) : null}
          </div>
        ),
      },
      {
        id: "booking-configuration",
        label: t("Booking Configuration"),
        keywords: ["booking window", "open time", "buffer", "maximum booking size", "days", "booking approval", "reservation confirmations", "confirmation email"],
        content: (
          <>
          <div className="activity-settings-grid activity-settings-grid--four">
            <Input
              value={moduleState.daysInAdvanceStart || ""}
              label={t("Booking Window Start")}
              id="daysInAdvanceStart"
              onChange={handleModuleInputChange}
              suffix="days"
              type="number"
              placeholder={0}
              disabled={moduleLocked}
            />
            <Input
              value={moduleState.daysInAdvanceEnd || ""}
              label={t("Booking Window End")}
              id="daysInAdvanceEnd"
              onChange={handleModuleInputChange}
              suffix="days"
              type="number"
              placeholder={0}
              disabled={moduleLocked}
            />
            <TimePick
              value={moduleState.openTime}
              onChange={timeString => setModuleState(prevState => ({ ...prevState, openTime: timeString }))}
              label={t("Open Time")}
              size="large"
              status={moduleState.openTime === undefined ? "warning" : undefined}
              disabled={moduleLocked}
            />
            <Input
              value={moduleState?.bufferWindow}
              label={t("Buffer Window")}
              id="bufferWindow"
              helpText={t("Bookings allowed up to (hours in advance)")}
              onChange={handleModuleInputChange}
              suffix="hours"
              placeholder={t("Buffer Window")}
              type="number"
              disabled={moduleLocked}
            />
            <div className="activity-settings-booking-size">
              <Input
                value={moduleState.maxBookingSize ?? ""}
                label={t("Maximum booking size")}
                id="maxBookingSize"
                helpText={t("Maximum guests allowed in a single reservation booking")}
                onChange={handleModuleInputChange}
                type="number"
                min={1}
                disabled={moduleLocked}
              />
              <Toggle
                labelRight={t("Require Booking Approval")}
                id="requireBookingApproval"
                checked={!!moduleState.requireBookingApproval}
                onChange={handleToggleChange}
                disabled={moduleLocked}
              />
            </div>
          </div>
          <div className="activity-settings-confirmations">
            <Toggle
              labelRight={t("Activity confirmations")}
              labelSupport={t("Alert recipients by email when a customer books their activity.")}
              id="reservationConfirmations"
              checked={!!moduleState.reservationConfirmations}
              onChange={handleToggleChange}
              disabled={moduleLocked}
            />
            {moduleState.reservationConfirmations ? (
              <TagInput
                label={t("Confirmation email")}
                id="reservation-confirmation-email"
                value={reservationEmailDraft}
                onChange={event => setReservationEmailDraft(event.target.value)}
                onKeyDown={addReservationEmail}
                placeholder={t("Enter email address...")}
                tags={parseEmails(moduleState.reservationConfirmationEmail)}
                onTagClick={removeReservationEmail}
                helpText={t("Add multiple emails by pressing Enter or comma.")}
                readOnly={moduleLocked}
              />
            ) : null}
          </div>
          </>
        ),
      },
      {
        id: "cancellation-policies",
        label: t("Cancellation Policies"),
        keywords: ["cancellation", "edit window", "hours"],
        content: (
          <div className="activity-settings-grid">
            <Input
              value={moduleState.cancellationWindow || ""}
              label={t("Cancellation Window")}
              id="cancellationWindow"
              helpText={t("Cancellations allowed up to (hours in advance)")}
              onChange={handleModuleInputChange}
              suffix="hours"
              placeholder={t("Cancellation window")}
              type="number"
              disabled={moduleLocked}
            />
            <Input
              value={moduleState.editWindow || ""}
              label={t("Edit Window")}
              id="editWindow"
              helpText={t("Changes allowed up to (hours in advance)")}
              onChange={handleModuleInputChange}
              suffix="hours"
              placeholder={t("Edit Window")}
              type="number"
              disabled={moduleLocked}
            />
          </div>
        ),
      },
      {
        id: "terms",
        label: t("Terms"),
        keywords: ["booking terms", "payment terms"],
        content: (
          <FormLayout>
            <FormLayout.Group>
              <TextEditor
                markdownText={moduleState.booking_terms ?? ""}
                markdownTextOnChange={handleBookingTermsChange}
                label={t("Booking Terms")}
                disabled={moduleLocked}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextEditor
                markdownText={moduleState.payment_terms ?? ""}
                markdownTextOnChange={handlePaymentTermsChange}
                label={t("Payment Terms")}
                disabled={moduleLocked}
              />
            </FormLayout.Group>
          </FormLayout>
        ),
      },
      facilityStore?.facility?.module_reservations
        ? {
            id: "booking-link",
            label: t("Booking Link"),
            keywords: ["booking link", "url", "portal", "copy"],
            content: (
              <div className="activity-booking-link">
                <Input
                  disabled
                  label={t("Booking link")}
                  value={bookingLink}
                  onChange={() => undefined}
                  trailingButtons={[
                    <Button key={1} onClick={() => writeTextToClipBoard(bookingLink)}>
                      <FontAwesomeIcon icon={["far", "copy"]} style={{ marginRight: "5px" }} /> {t("Copy")}
                    </Button>,
                  ]}
                />
              </div>
            ),
          }
        : null,
    ] as Array<TSection | null>
  ).filter((section): section is TSection => section != null);

  const filteredSections = settingsSections.filter(section => sectionMatchesSearch(section, debouncedSearch));

  return (
    <>
      <Page
        title={state?.module?.title ?? ""}
        subtitle={t("Manage activity type")}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Activity Types"),
            url: "/admin/settings/reservations/modules",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveModule,
          onCancel: () => {
            setModuleState(moduleStateBeforeChanges);
            setUnits(unitsBeforeChanges.map(unit => ({ ...unit })));
            setReservationEmailDraft("");
            setNewLocationName("");
            setEditingLocationKey(null);
            setAddingLocation(false);
          },
        }}
        primaryAction={{
          content: t("Delete"),
          action: () => updateDeleteModuleState({ isOpen: true }),
          disabled: !permissions?.reservations_module_edit,
        }}
        headerContent={
          <div className="activity-settings-search">
            <Input
              className="bg-white"
              labelHidden
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder={t("Search settings...")}
              onSearchClear={() => setSearchQuery("")}
            />
          </div>
        }
      >
        <div className="activity-settings-scroll">
          <ScrollTabsLayout
            showSectionTitles
            sections={filteredSections}
            emptyState={{
              title: t("Setting Not Found"),
              content: t("No matching setting found. Please readjust your search and try again."),
            }}
          />
        </div>


        <Portal isMounted={deleteModuleState?.isOpen}>
          <Popup
            open={deleteModuleState?.isOpen}
            title={t("Delete Activity Type")}
            description={t("Are you sure you want to delete this activity type? This cannot be undone")}
            type="warning"
            onCancel={closeDeleteModuleState}
            onOk={deleteModule}
          />
        </Portal>
        <Portal isMounted={deleteLocationState?.isOpen}>
          <Popup
            open={deleteLocationState?.isOpen}
            title={t("Delete location")}
            description={t("Are you sure you want to delete {{title}}? This cannot be undone", {
              title: deleteLocationState?.title ?? "",
            })}
            type="warning"
            onCancel={closeDeleteLocationState}
            onOk={() => void deleteLocation()}
          />
        </Portal>
      </Page>
    </>
  );
}
